import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { AuthContext, API } from '../App';
import { Users, FileText, MessageSquare, Mail, LogOut, TrendingUp, CheckCircle2, Loader2, Shield, Search, Clock, Key, Plus, XCircle, Eye, Download, Phone, Filter } from 'lucide-react';
import Logo from '../components/Logo';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminSuccessStories } from '../components/admin/AdminSuccessStories';
import { AdminBranding } from '../components/admin/AdminBranding';
import { AdminAffiliates } from '../components/admin/AdminAffiliates';

const ACCESS_TYPES = [
  { value: 'pro', label: 'Pro — AED 99' },
  { value: 'career_boost', label: 'Career Boost — AED 199' },
  { value: 'elite', label: 'Elite — AED 499' },
  { value: 'career_success_pro', label: 'Career Success Pro — AED 999' },
];

const DURATION_PRESETS = [
  { value: 30, label: '1 month (30 days)' },
  { value: 60, label: '2 months (60 days)' },
  { value: 90, label: '3 months (90 days)' },
  { value: 0, label: 'Plan default' },
];

export default function AdminDashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Grant access
  const [grantEmail, setGrantEmail] = useState('');
  const [grantTypes, setGrantTypes] = useState([]);
  const [grantNote, setGrantNote] = useState('');
  const [grantDuration, setGrantDuration] = useState(0); // 0 = plan default
  const [granting, setGranting] = useState(false);
  const [grantResult, setGrantResult] = useState(null);

  // User detail
  const [searchEmail, setSearchEmail] = useState('');
  const [userDetail, setUserDetail] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [extendDays, setExtendDays] = useState(30);
  const [extending, setExtending] = useState(false);
  const [resetPw, setResetPw] = useState('');
  const [resettingPw, setResettingPw] = useState(false);

  // Active subscribers
  const [subscribers, setSubscribers] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);

  // CV Leads
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadFilter, setLeadFilter] = useState('all');

  // Admin CV Fix Tool
  const [cvFixFile, setCvFixFile] = useState(null);
  const [cvFixing, setCvFixing] = useState(false);
  const [cvFixResult, setCvFixResult] = useState(null);
  const [cvFixTargetRole, setCvFixTargetRole] = useState('');
  const [cvFixTargetCountry, setCvFixTargetCountry] = useState('UAE');
  const [cvFixJD, setCvFixJD] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch { toast.error('Failed to load admin data'); }
    finally { setLoading(false); }
  };

  const fetchSubscribers = async () => {
    setSubsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/active-subscribers`);
      setSubscribers(res.data.subscribers || []);
    } catch { toast.error('Failed to load subscribers'); }
    finally { setSubsLoading(false); }
  };

  const fetchLeads = async (filter) => {
    setLeadsLoading(true);
    try {
      let params = {};
      if (filter === 'below50') params.score_below = 50;
      else if (filter === 'below70') params.score_below = 70;
      else if (filter === 'recent') params.days = 7;
      const res = await axios.get(`${API}/admin/cv-leads`, { params });
      setLeads(res.data.leads || []);
    } catch { toast.error('Failed to load leads'); }
    finally { setLeadsLoading(false); }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'subscribers' && subscribers.length === 0) fetchSubscribers();
    if (tab === 'leads' && leads.length === 0) fetchLeads('all');
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const handleSearchUser = async () => {
    if (!searchEmail.trim()) return;
    setSearchLoading(true); setUserDetail(null);
    try {
      const res = await axios.get(`${API}/admin/user-detail/${encodeURIComponent(searchEmail.trim())}`);
      setUserDetail(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'User not found'); }
    finally { setSearchLoading(false); }
  };

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    if (!grantEmail.trim()) { toast.error('Enter user email'); return; }
    if (grantTypes.length === 0) { toast.error('Select at least one access type'); return; }
    setGranting(true); setGrantResult(null);
    try {
      const payload = { email: grantEmail.trim(), access_types: grantTypes, note: grantNote.trim() || null };
      if (grantDuration && grantDuration > 0) payload.duration_days = grantDuration;
      const res = await axios.post(`${API}/admin/grant-access`, payload);
      setGrantResult({ success: true, message: res.data.message });
      toast.success('Access granted'); setGrantEmail(''); setGrantTypes([]); setGrantNote(''); setGrantDuration(0);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed';
      setGrantResult({ success: false, message: msg }); toast.error(msg);
    } finally { setGranting(false); }
  };

  const toggleGrantType = (val) => setGrantTypes(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);

  const handleExtend = async () => {
    if (!userDetail) return;
    setExtending(true);
    try {
      const res = await axios.post(`${API}/admin/extend-plan`, { email: userDetail.user.email, days: extendDays });
      toast.success(res.data.message); handleSearchUser();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setExtending(false); }
  };

  const handleResetPassword = async (sendEmail = false) => {
    if (!userDetail) return;
    setResettingPw(true);
    try {
      const body = { email: userDetail.user.email };
      if (sendEmail) body.send_reset_email = true;
      else {
        if (!resetPw || resetPw.length < 6) { toast.error('Min 6 chars'); setResettingPw(false); return; }
        body.new_password = resetPw;
      }
      const res = await axios.post(`${API}/admin/reset-user-password`, body);
      toast.success(res.data.message); setResetPw('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setResettingPw(false); }
  };

  const handleRevoke = async (entId) => {
    try {
      const res = await axios.post(`${API}/admin/revoke-access/${entId}`);
      toast.success(res.data.message);
      if (userDetail) handleSearchUser();
      if (subscribers.length > 0) fetchSubscribers();
    } catch { toast.error('Failed to revoke'); }
  };

  const handleResetCandidateLock = async () => {
    if (!userDetail) return;
    if (!window.confirm(`Reset candidate lock for ${userDetail.user.email}? This will allow them to use their package for a different CV.`)) return;
    try {
      const res = await axios.post(`${API}/admin/candidate-lock/reset/${encodeURIComponent(userDetail.user.email)}`);
      toast.success(res.data.message);
      handleSearchUser();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset lock'); }
  };

  const handleExportLeads = async () => {
    try {
      let params = {};
      if (leadFilter === 'below50') params.score_below = 50;
      else if (leadFilter === 'below70') params.score_below = 70;
      else if (leadFilter === 'recent') params.days = 7;
      const res = await axios.get(`${API}/admin/cv-leads/export`, { params });
      const blob = new Blob([res.data.csv], { type: 'text/csv' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = `cv_leads_${new Date().toISOString().slice(0,10)}.csv`; a.click();
      toast.success(`Exported ${res.data.total} leads`);
    } catch { toast.error('Export failed'); }
  };

  const copyToClipboard = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  const handleAdminCvFix = async () => {
    if (!cvFixFile) { toast.error('Upload a CV file first'); return; }
    setCvFixing(true); setCvFixResult(null);
    try {
      const formData = new FormData();
      formData.append('file', cvFixFile);
      if (cvFixTargetRole.trim()) formData.append('target_role', cvFixTargetRole.trim());
      if (cvFixTargetCountry.trim()) formData.append('target_country', cvFixTargetCountry.trim());
      if (cvFixJD.trim()) formData.append('job_description', cvFixJD.trim());
      const res = await axios.post(`${API}/admin/cv-fix-direct`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      setCvFixResult(res.data);
      toast.success('Gulf/UAE CV optimization complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate CV fix');
    } finally { setCvFixing(false); }
  };

  const handleDownloadAdminFix = async (fixId, format = 'pdf') => {
    try {
      const res = await axios.get(`${API}/admin/cv-fix-download${format === 'docx' ? '-docx' : ''}/${fixId}`, { responseType: 'blob' });
      const mime = format === 'docx' ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' : 'application/pdf';
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a'); a.href = url; a.download = `fixed_cv_${fixId}.${format}`; a.click();
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch { toast.error('Download failed'); }
  };


  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
  const isExpired = (d) => d && new Date(d) < new Date();

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#CE1126]"></div></div>;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'subscribers', label: 'Subscribers' },
    { id: 'leads', label: 'CV Leads' },
    { id: 'grant', label: 'Grant Access' },
    { id: 'cvfix', label: 'ATS Fix Tool' },
    { id: 'stories', label: 'Success Stories' },
    { id: 'branding', label: 'Branding / Community' },
    { id: 'affiliates', label: 'Affiliates' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Logo linkTo="/dashboard" />
          <nav className="flex items-center space-x-4">
            <Link to="/dashboard"><Button variant="ghost" size="sm">Dashboard</Button></Link>
            <Button variant="ghost" size="sm" onClick={handleLogout}><LogOut className="w-4 h-4 mr-2" />Logout</Button>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
          <div className="flex gap-2 border-b pb-2 flex-wrap">
            {tabs.map(t => (
              <button key={t.id} onClick={() => handleTabChange(t.id)} data-testid={`admin-tab-${t.id}`}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === t.id ? 'bg-white border border-b-0 text-[#CE1126]' : 'text-gray-500 hover:text-gray-700'}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* === OVERVIEW TAB === */}
        {activeTab === 'overview' && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
              {[
                { icon: Users, color: 'text-[#CE1126]', label: 'Users', val: stats?.total_users },
                { icon: TrendingUp, color: 'text-[#009639]', label: 'Active Subs', val: stats?.active_subscriptions },
                { icon: FileText, color: 'text-blue-600', label: 'CV Checks', val: stats?.total_cv_checks },
                { icon: MessageSquare, color: 'text-purple-600', label: 'Tests', val: stats?.total_interview_tests },
                { icon: Mail, color: 'text-yellow-600', label: 'Newsletter', val: stats?.total_newsletter_subscribers },
                { icon: Shield, color: 'text-emerald-600', label: 'Active Ent.', val: stats?.active_entitlements },
              ].map((s, i) => (
                <Card key={i} className="p-4"><s.icon className={`w-6 h-6 ${s.color} mb-1`} /><p className="text-xs text-gray-500">{s.label}</p><p className="text-2xl font-bold">{s.val || 0}</p></Card>
              ))}
            </div>

            {/* User Search */}
            <Card className="p-6 mb-8" data-testid="user-search-section">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><Search className="w-5 h-5" /> User Management</h2>
              <div className="flex gap-2 mb-4">
                <input type="email" value={searchEmail} onChange={e => setSearchEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearchUser()}
                  placeholder="Search user by email..." className="flex-1 h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]" data-testid="user-search-input" />
                <Button onClick={handleSearchUser} disabled={searchLoading} className="btn-uae-red" data-testid="user-search-btn">
                  {searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                </Button>
              </div>
              {userDetail && (
                <div className="border rounded-lg p-4 space-y-4" data-testid="user-detail-panel">
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div><p className="text-xs text-gray-500">Name</p><p className="font-medium">{userDetail.user.name}</p></div>
                    <div><p className="text-xs text-gray-500">Email</p><p className="font-medium text-sm">{userDetail.user.email}</p></div>
                    <div><p className="text-xs text-gray-500">Plan</p><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${userDetail.user.subscription_plan === 'pro' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>{userDetail.user.subscription_plan || 'free'}</span></div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-2 font-medium">Usage</p>
                    <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
                      {Object.entries(userDetail.usage).map(([k, v]) => (
                        <div key={k} className="text-center p-2 bg-gray-50 rounded"><p className="text-lg font-bold">{v}</p><p className="text-[10px] text-gray-500">{k.replace(/_/g, ' ')}</p></div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-2 font-medium">Entitlements</p>
                    {userDetail.entitlements.length === 0 ? <p className="text-sm text-gray-400">None</p> : (
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {userDetail.entitlements.map(e => (
                          <div key={e.id} className={`flex items-center justify-between p-2 rounded text-xs border ${e.status === 'active' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                            <div><span className="font-medium">{e.product_type}</span>
                              {e.expires_at && <span className={`ml-2 ${isExpired(e.expires_at) ? 'text-red-500' : 'text-gray-500'}`}>{isExpired(e.expires_at) ? 'Expired' : `Exp: ${fmtDate(e.expires_at)}`}</span>}
                              {e.usage_remaining && Object.keys(e.usage_remaining).length > 0 && <span className="ml-2 text-blue-600">{Object.entries(e.usage_remaining).map(([k,v])=>`${k}=${v}`).join(', ')}</span>}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className={`px-1.5 py-0.5 rounded ${e.status === 'active' ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'}`}>{e.status}</span>
                              {e.status === 'active' && <button onClick={() => handleRevoke(e.id)} className="text-red-400 hover:text-red-600" title="Revoke"><XCircle className="w-3.5 h-3.5" /></button>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {userDetail.candidate_lock && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-2">
                      <p className="text-xs font-medium text-yellow-800 mb-1">Candidate Lock (Anti-Sharing)</p>
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-yellow-700">
                          <span className="font-medium">{userDetail.candidate_lock.candidate_name || 'Unknown'}</span>
                          {userDetail.candidate_lock.candidate_email && <span className="ml-2">| {userDetail.candidate_lock.candidate_email}</span>}
                          {userDetail.candidate_lock.candidate_phone && <span className="ml-2">| {userDetail.candidate_lock.candidate_phone}</span>}
                        </div>
                        <Button size="sm" variant="outline" onClick={handleResetCandidateLock} className="text-xs border-yellow-400 text-yellow-800 hover:bg-yellow-100">Reset Lock</Button>
                      </div>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t">
                    <div>
                      <p className="text-xs text-gray-500 mb-1 font-medium flex items-center gap-1"><Clock className="w-3 h-3" /> Extend</p>
                      <div className="flex gap-2">
                        {[30, 60, 90].map(d => (
                          <Button key={d} size="sm" variant={extendDays === d ? 'default' : 'outline'} onClick={() => setExtendDays(d)} className="text-xs">{d}d</Button>
                        ))}
                        <Button size="sm" onClick={handleExtend} disabled={extending} className="btn-uae-red text-xs">{extending ? '...' : 'Extend'}</Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1 font-medium flex items-center gap-1"><Key className="w-3 h-3" /> Reset Password</p>
                      <div className="flex gap-2">
                        <input type="text" value={resetPw} onChange={e => setResetPw(e.target.value)} placeholder="New password..." className="flex-1 h-8 rounded border border-gray-300 px-2 text-sm" data-testid="admin-reset-pw-input" />
                        <Button size="sm" onClick={() => handleResetPassword(false)} disabled={resettingPw} variant="outline" data-testid="admin-reset-pw-btn">Set</Button>
                      </div>
                      <Button size="sm" variant="ghost" className="text-xs text-blue-600 mt-1" onClick={() => handleResetPassword(true)} disabled={resettingPw}>Send reset email</Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Users Table */}
            <Card className="p-6">
              <h2 className="text-xl font-bold mb-4">Recent Users</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b"><tr className="text-left"><th className="pb-2">Name</th><th className="pb-2">Email</th><th className="pb-2">Plan</th><th className="pb-2">CV</th><th className="pb-2">Joined</th><th className="pb-2"></th></tr></thead>
                  <tbody>
                    {users.slice(0, 50).map(u => (
                      <tr key={u.id} className="border-b">
                        <td className="py-2">{u.name}</td>
                        <td className="py-2 text-gray-600 text-xs">{u.email}</td>
                        <td className="py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${u.subscription_plan === 'pro' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>{u.subscription_plan || 'free'}</span></td>
                        <td className="py-2">{u.cv_checks_count}</td>
                        <td className="py-2 text-gray-500 text-xs">{fmtDate(u.created_at)}</td>
                        <td className="py-2"><button onClick={() => { setSearchEmail(u.email); setUserDetail(null); setTimeout(() => handleSearchUser(), 100); }} className="text-xs text-blue-600 hover:underline"><Eye className="w-3 h-3 inline" /> View</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}

        {/* === SUBSCRIBERS TAB === */}
        {activeTab === 'subscribers' && (
          <Card className="p-6" data-testid="subscribers-section">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Active Subscribers ({subscribers.length})</h2>
              <Button variant="outline" size="sm" onClick={fetchSubscribers} disabled={subsLoading}>
                {subsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
              </Button>
            </div>
            {subsLoading ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b"><tr className="text-left"><th className="pb-2">Name</th><th className="pb-2">Email</th><th className="pb-2">Plan</th><th className="pb-2">Entitlement</th><th className="pb-2">Expires</th><th className="pb-2">Usage</th><th className="pb-2">Actions</th></tr></thead>
                  <tbody>
                    {subscribers.map(s => (
                      <tr key={s.user_id} className="border-b">
                        <td className="py-2 font-medium">{s.name}</td>
                        <td className="py-2 text-xs text-gray-600">{s.email}</td>
                        <td className="py-2"><span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">{s.plan}</span></td>
                        <td className="py-2 text-xs">{s.entitlements.map(e => e.type.replace('manual_', '')).join(', ')}</td>
                        <td className="py-2 text-xs">{s.entitlements[0]?.expires_at ? fmtDate(s.entitlements[0].expires_at) : 'Never'}</td>
                        <td className="py-2 text-xs">CV:{s.cv_checks} Tests:{s.interview_tests}</td>
                        <td className="py-2">
                          <div className="flex gap-1">
                            <button onClick={() => { setSearchEmail(s.email); setActiveTab('overview'); setTimeout(() => handleSearchUser(), 200); }} className="text-xs text-blue-600 hover:underline">View</button>
                            {s.entitlements[0]?.id && s.entitlements[0]?.status === 'active' && (
                              <button onClick={() => handleRevoke(s.entitlements[0].id)} className="text-xs text-red-500 hover:underline ml-2">Revoke</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {subscribers.length === 0 && <p className="text-center text-gray-400 py-8">No active subscribers yet</p>}
              </div>
            )}
          </Card>
        )}

        {/* === CV LEADS TAB === */}
        {activeTab === 'leads' && (
          <Card className="p-6" data-testid="cv-leads-section">
            <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2"><Phone className="w-5 h-5" /> CV Leads ({leads.length})</h2>
              <div className="flex gap-2 items-center">
                <Filter className="w-4 h-4 text-gray-400" />
                {[
                  { id: 'all', label: 'All' },
                  { id: 'below70', label: 'Score < 70' },
                  { id: 'below50', label: 'Score < 50' },
                  { id: 'recent', label: 'Last 7 days' },
                ].map(f => (
                  <button key={f.id} onClick={() => { setLeadFilter(f.id); fetchLeads(f.id); }}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${leadFilter === f.id ? 'border-[#CE1126] bg-red-50 text-[#CE1126]' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                    {f.label}
                  </button>
                ))}
                <Button size="sm" variant="outline" onClick={handleExportLeads} data-testid="export-leads-btn">
                  <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
                </Button>
              </div>
            </div>
            {leadsLoading ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b"><tr className="text-left"><th className="pb-2">Name</th><th className="pb-2">Email</th><th className="pb-2">Phone</th><th className="pb-2">ATS Score</th><th className="pb-2">Plan</th><th className="pb-2">Date</th><th className="pb-2">Actions</th></tr></thead>
                  <tbody>
                    {leads.map((l, i) => (
                      <tr key={l.id || i} className="border-b">
                        <td className="py-2 font-medium">{l.cv_full_name || l.user_name || '-'}</td>
                        <td className="py-2 text-xs text-gray-600">{l.cv_email || l.user_email || '-'}</td>
                        <td className="py-2 text-xs">{l.cv_phone || '-'}</td>
                        <td className="py-2">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${l.ats_score < 50 ? 'bg-red-100 text-red-700' : l.ats_score < 70 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>{l.ats_score}</span>
                        </td>
                        <td className="py-2 text-xs">{l.subscription_plan || 'free'}</td>
                        <td className="py-2 text-xs text-gray-500">{fmtDate(l.created_at)}</td>
                        <td className="py-2">
                          <div className="flex gap-2">
                            {(l.cv_email || l.user_email) && <button onClick={() => copyToClipboard(l.cv_email || l.user_email, 'Email')} className="text-xs text-blue-600 hover:underline">Copy Email</button>}
                            {l.cv_phone && <button onClick={() => copyToClipboard(l.cv_phone, 'Phone')} className="text-xs text-green-600 hover:underline">Copy Phone</button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {leads.length === 0 && <p className="text-center text-gray-400 py-8">No CV leads yet. Leads are captured when users run CV checks.</p>}
              </div>
            )}
          </Card>
        )}

        {/* === GRANT ACCESS TAB === */}
        {activeTab === 'grant' && (
          <Card className="p-6" data-testid="grant-access-section">
            <div className="flex items-center gap-2 mb-4"><Shield className="w-5 h-5 text-[#CE1126]" /><h2 className="text-xl font-bold">Grant User Access</h2></div>
            <form onSubmit={handleGrantAccess} className="space-y-4 max-w-2xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User Email *</label>
                <input type="email" value={grantEmail} onChange={e => setGrantEmail(e.target.value)} placeholder="user@example.com"
                  className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]" data-testid="grant-email-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Access Types * <span className="text-gray-400 text-xs">(select one or more)</span></label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {ACCESS_TYPES.map(a => (
                    <label key={a.value} className={`flex items-center gap-2 p-2.5 rounded-md border cursor-pointer text-sm transition-colors ${grantTypes.includes(a.value) ? 'border-[#CE1126] bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input type="checkbox" checked={grantTypes.includes(a.value)} onChange={() => toggleGrantType(a.value)} className="accent-[#CE1126]" />
                      {a.label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                <select value={grantDuration} onChange={e => setGrantDuration(parseInt(e.target.value, 10))}
                  className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]" data-testid="grant-duration-select">
                  {DURATION_PRESETS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note <span className="text-gray-400">(optional)</span></label>
                <input type="text" value={grantNote} onChange={e => setGrantNote(e.target.value)} placeholder="e.g. AED 149 WhatsApp payment"
                  className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]" data-testid="grant-note-input" />
              </div>
              <Button type="submit" className="btn-uae-red" disabled={granting} data-testid="grant-access-btn">
                {granting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Granting...</> : `Grant Access${grantTypes.length > 0 ? ` (${grantTypes.length})` : ''}`}
              </Button>
            </form>
            {grantResult && (
              <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 text-sm ${grantResult.success ? 'bg-green-50 text-[#009639]' : 'bg-red-50 text-red-600'}`} data-testid="grant-result">
                <CheckCircle2 className="w-4 h-4" />{grantResult.message}
              </div>
            )}
          </Card>
        )}

        {/* === ATS FIX TOOL TAB === */}
        {activeTab === 'cvfix' && (
          <Card className="p-6" data-testid="admin-cvfix-section">
            <div className="flex items-center gap-2 mb-4"><FileText className="w-5 h-5 text-[#CE1126]" /><h2 className="text-xl font-bold">Admin ATS CV Fix Tool</h2></div>
            <p className="text-sm text-gray-500 mb-4">Upload any CV directly. Uses the same Phase 1A Gulf/UAE expert CV engine as the customer flow — analyze, optimize, and download PDF or DOCX.</p>
            <div className="max-w-lg space-y-3 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload CV (PDF, DOC, DOCX)</label>
                <input type="file" accept=".pdf,.doc,.docx" onChange={e => {
                    const f = e.target.files[0];
                    if (f && f.size > 10 * 1024 * 1024) {
                      toast.error('File too large. Maximum allowed size is 10 MB.');
                      e.target.value = '';
                      return;
                    }
                    setCvFixFile(f);
                  }}
                  className="w-full text-sm border border-gray-300 rounded-md p-2 file:mr-3 file:rounded file:border-0 file:bg-[#CE1126] file:text-white file:px-3 file:py-1 file:text-sm file:cursor-pointer"
                  data-testid="admin-cvfix-upload" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Target Role (optional)</label>
                  <input value={cvFixTargetRole} onChange={e => setCvFixTargetRole(e.target.value)} placeholder="e.g. Sales Executive"
                    className="w-full h-9 rounded border border-gray-300 px-3 text-sm" data-testid="admin-cvfix-role-input" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Target Country</label>
                  <input value={cvFixTargetCountry} onChange={e => setCvFixTargetCountry(e.target.value)} placeholder="UAE"
                    className="w-full h-9 rounded border border-gray-300 px-3 text-sm" data-testid="admin-cvfix-country-input" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Job Description (optional)</label>
                <textarea value={cvFixJD} onChange={e => setCvFixJD(e.target.value)} rows={2} placeholder="Paste target job description..."
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm" data-testid="admin-cvfix-jd-input" />
              </div>
              <Button onClick={handleAdminCvFix} disabled={cvFixing || !cvFixFile} className="btn-uae-red" data-testid="admin-cvfix-btn">
                {cvFixing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing & Optimizing...</> : 'Upload & Generate ATS Fix'}
              </Button>
            </div>
            {cvFixResult && cvFixResult.success && (
              <div className="border rounded-lg p-5 bg-green-50 space-y-4">
                <p className="text-sm text-green-800 font-medium">{cvFixResult.message}</p>
                {/* Before / After ATS Score */}
                {cvFixResult.analysis && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-lg p-3 text-center border">
                      <p className="text-xs text-gray-500">Original ATS Score</p>
                      <p className={`text-2xl font-bold ${cvFixResult.analysis.overall_score >= 70 ? 'text-green-700' : 'text-red-600'}`} data-testid="admin-cvfix-before-score">{cvFixResult.analysis.overall_score}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border">
                      <p className="text-xs text-gray-500">Improved ATS Score</p>
                      <p className="text-2xl font-bold text-green-700" data-testid="admin-cvfix-after-score">{cvFixResult.after_score ?? '—'}</p>
                    </div>
                  </div>
                )}
                {/* CV Info */}
                <div className="text-xs text-gray-600">
                  <p>Name: <span className="font-medium">{cvFixResult.fixed_cv?.full_name}</span></p>
                  {cvFixResult.fixed_cv?.email && <p>Email: {cvFixResult.fixed_cv.email}</p>}
                </div>
                {/* Suggestions */}
                {cvFixResult.analysis?.suggestions?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-700 mb-1">Key Improvements:</p>
                    <ul className="text-xs text-gray-600 space-y-0.5">
                      {cvFixResult.analysis.suggestions.map((s, i) => <li key={i}>- {s}</li>)}
                    </ul>
                  </div>
                )}
                {/* Download */}
                <div className="flex gap-2">
                  <Button onClick={() => handleDownloadAdminFix(cvFixResult.fixed_cv.id, 'pdf')} className="btn-uae-red" data-testid="admin-cvfix-download-pdf">
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                  <Button onClick={() => handleDownloadAdminFix(cvFixResult.fixed_cv.id, 'docx')} variant="outline" data-testid="admin-cvfix-download-docx">
                    <Download className="w-4 h-4 mr-2" /> Download DOCX
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* === SUCCESS STORIES TAB === */}
        {activeTab === 'stories' && <AdminSuccessStories />}

        {/* === BRANDING / COMMUNITY TAB === */}
        {activeTab === 'branding' && <AdminBranding />}

        {/* === AFFILIATES TAB === */}
        {activeTab === 'affiliates' && <AdminAffiliates />}
      </div>
    </div>
  );
}
